/**
 * Modificateurs d'économie pilotables en live depuis le panel admin.
 *
 * Principe : au lieu de recaler des tables de prix à la main à chaque nerf, on injecte
 * les modificateurs **dans les primitives** (or/kill, prix bonbon, coût bannière…). Tout
 * ce qui en dérive déjà — plafonds de crédit or, anti-abus, estimation offline, HUD —
 * suit automatiquement et reste cohérent.
 *
 * Deux cadrans distincts (c'est la nuance qui manquait au « ratio unique ») :
 *  - `goldFaucetMult`    : échelle des nombres (inflation visuelle)
 *  - `pricePressureMult` : nombre de kills nécessaires par achat = la difficulté réelle
 * Monter les deux d'autant laisse la difficulté inchangée : seuls les chiffres grossissent.
 *
 * Valeurs neutres par défaut (tout à 1, flags à leur comportement historique) : tant que
 * l'admin n'a rien touché, le jeu se comporte exactement comme avant.
 *
 * ⚠ Écrit en `.mjs` **volontairement**, et pas en `.ts` avec un miroir comme les autres
 * modules partagés. Ce module porte un état mutable (le registre ambiant ci-dessous) :
 * deux copies compilées = deux registres désynchronisés. Client, serveur et tests Node
 * importent donc tous ce fichier-ci. Les types vivent dans `economy_modifiers.d.mts`.
 */

export const DEFAULT_ECONOMY_MODIFIERS = {
  goldFaucetMult: 1,
  wildGoldMult: 1,
  sanctuaryGoldMult: 1,
  towerBossGoldMult: 1,
  pricePressureMult: 1,
  gachaPriceMult: 1,
  candyPriceMult: 1,
  evoItemPriceMult: 1,
  casinoBetMult: 1,
  goldWellCostMult: 1,
  ascensionGoldCurveMult: 1,
  ascensionGoldBoostDoubleCount: true,
  goldStackCap: 0,
  antiAbuseHeadroomMult: 1,
  antiAbuseIncomeMarginMult: 1,
};

export const ECONOMY_MODIFIER_BOUNDS = {
  goldFaucetMult: { min: 0.01, max: 100 },
  wildGoldMult: { min: 0.01, max: 100 },
  sanctuaryGoldMult: { min: 0.01, max: 100 },
  towerBossGoldMult: { min: 0.01, max: 100 },
  pricePressureMult: { min: 0.01, max: 100 },
  gachaPriceMult: { min: 0.01, max: 100 },
  candyPriceMult: { min: 0.01, max: 100 },
  evoItemPriceMult: { min: 0.01, max: 100 },
  casinoBetMult: { min: 0.01, max: 100 },
  goldWellCostMult: { min: 0.01, max: 100 },
  ascensionGoldCurveMult: { min: 0, max: 10 },
  goldStackCap: { min: 0, max: 10000 },
  antiAbuseHeadroomMult: { min: 0.25, max: 20 },
  antiAbuseIncomeMarginMult: { min: 0.25, max: 20 },
};

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

export function normalizeEconomyModifiers(input) {
  const src = input ?? {};
  const out = { ...DEFAULT_ECONOMY_MODIFIERS };
  for (const key of Object.keys(ECONOMY_MODIFIER_BOUNDS)) {
    const bounds = ECONOMY_MODIFIER_BOUNDS[key];
    if (src[key] === undefined || src[key] === null) continue;
    out[key] = clampNumber(src[key], bounds.min, bounds.max, DEFAULT_ECONOMY_MODIFIERS[key]);
  }
  if (
    src.ascensionGoldBoostDoubleCount !== undefined &&
    src.ascensionGoldBoostDoubleCount !== null
  ) {
    out.ascensionGoldBoostDoubleCount = Boolean(src.ascensionGoldBoostDoubleCount);
  }
  return out;
}

export function isNeutralEconomyModifiers(mods) {
  return (
    Object.keys(ECONOMY_MODIFIER_BOUNDS).every(
      (k) => mods[k] === DEFAULT_ECONOMY_MODIFIERS[k],
    ) &&
    mods.ascensionGoldBoostDoubleCount ===
      DEFAULT_ECONOMY_MODIFIERS.ascensionGoldBoostDoubleCount
  );
}

// ── Registre ambiant ────────────────────────────────────────────────────────
// Les primitives d'économie (`getWildGoldReward`, `getCandyCost`…) sont des fonctions
// pures appelées depuis ~40 endroits, client et serveur. Plutôt que d'ajouter un
// paramètre partout, elles lisent ce registre. Client : mis à jour au /load puis à chaque
// push WebSocket `economy:update`. Serveur : mis à jour à chaque rafraîchissement du cache
// `app_settings` (invalidé cross-worker via Redis pub/sub).
//
// Fenêtre de cohérence : lors d'un changement admin, un worker peut calculer avec
// l'ancienne valeur pendant le temps d'invalidation (< 1 s). Les deux valeurs étant des
// configurations valides, l'impact se limite à un tick de combat.
//
// Les composants Vue qui *affichent* un prix doivent passer les modificateurs
// explicitement (paramètre `mods`) depuis `usePlayerStore.economyModifiers`, sinon
// l'affichage ne se rafraîchit pas : ce registre n'est pas réactif.

let ambient = { ...DEFAULT_ECONOMY_MODIFIERS };
let ambientRevision = 0;

export function setEconomyModifiers(mods) {
  ambient = normalizeEconomyModifiers(mods);
  ambientRevision += 1;
  return ambient;
}

export function getEconomyModifiers() {
  return ambient;
}

export function getEconomyModifiersRevision() {
  return ambientRevision;
}

export function resetEconomyModifiers() {
  ambient = { ...DEFAULT_ECONOMY_MODIFIERS };
  ambientRevision += 1;
}

export function wildGoldFactor(mods = ambient) {
  return mods.goldFaucetMult * mods.wildGoldMult;
}

export function sanctuaryGoldFactor(mods = ambient) {
  return mods.goldFaucetMult * mods.sanctuaryGoldMult;
}

export function towerBossGoldFactor(mods = ambient) {
  return mods.goldFaucetMult * mods.towerBossGoldMult;
}

export function gachaPriceFactor(mods = ambient) {
  return mods.pricePressureMult * mods.gachaPriceMult;
}

export function candyPriceFactor(mods = ambient) {
  return mods.pricePressureMult * mods.candyPriceMult;
}

export function evoItemPriceFactor(mods = ambient) {
  return mods.pricePressureMult * mods.evoItemPriceMult;
}

export function casinoBetFactor(mods = ambient) {
  return mods.pricePressureMult * mods.casinoBetMult;
}

export function goldWellCostFactor(mods = ambient) {
  return mods.pricePressureMult * mods.goldWellCostMult;
}

export function applyAscensionGoldCurve(multiplier, mods = ambient) {
  const m = Number(multiplier);
  if (!Number.isFinite(m)) return 1;
  if (mods.ascensionGoldCurveMult === 1) return m;
  return 1 + (m - 1) * mods.ascensionGoldCurveMult;
}

export function capGoldStack(stack, mods = ambient) {
  const s = Number(stack);
  if (!Number.isFinite(s) || s <= 0) return 0;
  if (mods.goldStackCap <= 0) return s;
  return Math.min(s, mods.goldStackCap);
}

export function scalePrice(base, factor) {
  const b = Number(base);
  if (!Number.isFinite(b) || b <= 0) return 0;
  return Math.max(1, Math.round(b * factor));
}

export function scaleReward(base, factor) {
  const b = Number(base);
  if (!Number.isFinite(b) || b <= 0) return 0;
  return Math.max(1, Math.round(b * factor));
}
